import {useGrpcRequestMutation} from '@aboutyou/data-fetcher';
import {
  Config,
  Device,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/config.types';
import {ABTestService_TrackABTestLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/ab_tests/service.grpc.lazy';
import {TrackABTestRequest} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/ab_tests/service.types';
import {waitUntilCustomerDataEvent} from '@aboutyou/tracking';
import {useInViewEffect} from '@aboutyou/ui-components';
import React, {useCallback, useMemo, useRef} from 'react';
import type {CheckoutExperiment} from '../hooks/useCheckoutEntryDetails';
import {dispatchCheckoutTrackingEvent} from './CheckoutTrackingListener';
// eslint-disable-next-line monorepo/no-internal-import
import {useCOTracking} from '@aboutyou/tracking/src/utils/coTracking';

const activated: Record<string, boolean> = {};

export const resetActivatedExperiments = () => {
  for (const key in activated) {
    if (activated[key]) {
      activated[key] = false;
    }
  }
};

export const ExperimentActivationTriggerSlot: React.FC<{
  experiments: CheckoutExperiment[];
  authToken: string;
  appClientVersion?: string;
}> = ({experiments, authToken, appClientVersion}) => {
  const {trackingDevice} = useCOTracking();
  const trackAbTest = useGrpcRequestMutation(ABTestService_TrackABTestLazy, {
    retry: false,
  });

  const deviceOs = useMemo(() => {
    if (trackingDevice === 'ios-app') {
      return 'iOS';
    }
    if (trackingDevice === 'android-app') {
      return 'Android';
    }
    return 'Web';
  }, [trackingDevice]);

  const requestConfig = useMemo(() => {
    if (trackingDevice === 'ios-app') {
      return {
        device: Device.IOS_APP,
        clientVersion: appClientVersion,
      } as Config;
    }
    if (trackingDevice === 'android-app') {
      return {
        device: Device.ANDROID_APP,
        clientVersion: appClientVersion,
      } as Config;
    }
  }, [trackingDevice, appClientVersion]);

  // Keep the latest props/derived values in a ref so the in-view callback can
  // stay referentially stable. Without this, the inline callback changes on
  // every render, which tears down and recreates the IntersectionObserver,
  // re-firing on the already-in-view element and defeating `triggerOnce`.
  const latest = useRef({experiments, authToken, deviceOs, requestConfig});
  latest.current = {experiments, authToken, deviceOs, requestConfig};

  const handleInView = useCallback(
    (inView: boolean) => {
      if (!inView) return;

      const {experiments, authToken, deviceOs, requestConfig} = latest.current;

      // Only track BNPL experiments in AB test tracking
      const bnplExperiments = experiments.filter(({experimentKey}) =>
        experimentKey.includes('bnpl'),
      );

      for (const {experimentKey, variantKey} of bnplExperiments) {
        if (activated[experimentKey]) continue;
        activated[experimentKey] = true;

        const activationTimestamp = Date.now();

        waitUntilCustomerDataEvent(() => {
          dispatchCheckoutTrackingEvent({
            event: 'experiment_activation',
            experiment_name: experimentKey,
            experiment_variant: variantKey,
            event_timestamp_ay: activationTimestamp,
          });
        });

        trackAbTest.mutateAsync({
          ...(requestConfig ? {config: requestConfig} : {}),
          session: {
            authToken,
          },
          trackingEvents: [
            {
              event: {
                $case: 'checkoutActivation',
                checkoutActivation: {
                  experimentKey,
                  eventTimestamp: new Date(activationTimestamp),
                  deviceOs,
                },
              },
            },
          ],
        } as TrackABTestRequest);
      }
    },
    // `trackAbTest` is a stable mutation handle; everything else is read from
    // the ref, so the callback never changes identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {ref} = useInViewEffect<HTMLDivElement>(handleInView, {
    threshold: 0.1,
    triggerOnce: true,
  });

  return <div slot="slot_payment_options_top" ref={ref}></div>;
};
